import { useMemo } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import type { SvgIconComponent } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  buildLmsEnrolmentLogEntries,
  type LmsEnrolmentForLogs,
  type LmsEnrolmentLogKey,
} from "@/components/lms/lmsEnrolmentLogs";
import styles from "@/components/lms/lmsEnrolmentLogsView.module.css";

const LOG_ICONS: Record<LmsEnrolmentLogKey, SvgIconComponent> = {
  invited_on: MailOutlineIcon,
  started_on: PlayCircleOutlineIcon,
  complete_by: ScheduleOutlinedIcon,
  completed_on: CheckCircleOutlineIcon,
  certificate_last_downloaded_on: FileDownloadOutlinedIcon,
  certificate_expires_on: EventOutlinedIcon,
};

interface Props {
  enrolment: LmsEnrolmentForLogs | null;
}

export default function LmsEnrolmentLogsView({ enrolment }: Props) {
  const { t } = useTranslation();
  const entries = useMemo(
    () => buildLmsEnrolmentLogEntries(enrolment),
    [enrolment],
  );

  if (entries.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateTitle}>
          {t("lms_enrolment_logs_empty_title")}
        </p>
        <p className={styles.emptyStateDescription}>
          {t("lms_enrolment_logs_empty_description")}
        </p>
      </div>
    );
  }

  return (
    <ol className={styles.timeline}>
      {entries.map((entry, index) => {
        const Icon = LOG_ICONS[entry.key];

        return (
          <li key={entry.key} className={styles.timelineItem}>
            <div className={styles.markerColumn}>
              <div className={styles.marker}>
                <Icon fontSize="inherit" />
              </div>
              {index < entries.length - 1 && (
                <div className={styles.connector} aria-hidden="true" />
              )}
            </div>
            <div className={styles.entryCard}>
              <div className={styles.entryLabel}>{t(entry.labelKey)}</div>
              <div className={styles.entryValue}>{entry.value}</div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
